import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Form, Typography } from 'antd';
import { useDispatch } from 'react-redux';
import { FiLoader } from 'react-icons/fi';
import * as actions from '@actions';
import useGDPRConfirmation from '@hooks/useGDPRConfirmation';
import { ConfirmationModal } from 'vl-common/src/components/Modals';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionOutcome } from '@hooks/useSessionOutcome';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEligibilityChecks } from '@hooks/useEligibilityChecks';
import { CreateF2FTaskModal } from '@components/Tasks/CreateF2FTask';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import SendGDPREmailFormItem from './SendGDPREmailFormItem';

const F2fCompletedFields = ({
  onSelectAppointmentOutcome
}: {
  onSelectAppointmentOutcome: (
    value: string,
    authInfo?: { auth: boolean; authStart: Date },
    overrideAuthAsFalse?: boolean
  ) => Promise<void>;
}) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [warnRemoveModalOpen, setWarnRemoveModalOpen] = useState(false);
  const [alert, setAlert] = useState({
    message: '',
    show: false
  });

  const [f2fOpen, setf2fOpen] = useState(false);
  const [auth] = useSessionFormState('auth');
  const [f2fComplete, setf2fComplete] = useSessionFormState('f2fComplete');
  const [, setF2fReferralPosted] = useSessionFormState('f2fReferralPosted');
  const [, setPostedReferralStartDate] = useSessionFormState('postedReferralStartDate');

  const { appointment } = useSessionAppointment();
  const {
    appointmentVersion,
    outcome: appointmentOutcome,
    outcomeFlags,
    defaultAndDisableSelfBook
  } = useSessionOutcome();

  const outcomeUsesReferral = appointmentVersion === 1 ? !outcomeFlags?.appt_raised_task_flag : false;
  const isCompleted = !!appointment.appointment_completed;
  const selfBook =
    appointmentVersion === 1 ? !!outcomeFlags?.appt_auth_f2f_flag && (auth || defaultAndDisableSelfBook) : false;

  const { refetch: refetchEligiblityChecks } = useEligibilityChecks();

  const { data: appointmentsMeta } = useAppointmentMeta();
  const appointmentMeta = appointmentsMeta?.appt_outcomes;

  const { needsGDPREmailSending, sendGDPREmail, GDPRConfirmationSent } = useGDPRConfirmation(
    appointment,
    appointmentOutcome,
    appointmentMeta
  );

  const { data: patientInfo } = useQuery({
    queryKey: ['/referrers/patients/', appointment.clinician_user_guid, appointment.patient_user_guid],
    queryFn: () => dispatch(actions.getPatientDetails(appointment.clinician_user_guid, appointment.patient_user_guid))
  });

  const { mutate: removeSelfBookAuthAfterReferral } = useMutation({
    mutationFn: async () => {
      if (!appointmentOutcome) return;
      await onSelectAppointmentOutcome(appointmentOutcome, undefined, true);
    },
    onSuccess: () => {
      setF2fReferralPosted(false);
      setPostedReferralStartDate(undefined);
      setf2fComplete(false);
      refetchEligiblityChecks();
    }
  });

  const pdfPreview = async () => {
    setPreviewLoading(true);
    const request = {
      preview: true,
      letter_code: 'F2F_SPEC',
      letter_template_type: ''
    };
    setAlert({
      show: false,
      message: ''
    });
    const response = await dispatch(
      actions.saveLamdaPDF(
        appointment.clinician_user_guid,
        appointment.appointment_id,
        appointment.patient_user_guid,
        request
      ),
      false
    );

    if (!response.pdf) {
      setAlert({
        show: true,
        message: 'Failed to generate letter preview. Please try again.'
      });
      setPreviewLoading(false);
      return;
    }
    // Convert to blob
    const byteCharacters = atob(response.pdf);

    const byteNumbers = Array.from({ length: byteCharacters.length }, (_, i) => byteCharacters.charCodeAt(i));

    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const newBlob = new Blob([blob], { type: 'application/pdf' });
    const blobURL = URL.createObjectURL(newBlob);
    setPreviewLoading(false);
    window.open(blobURL);
  };

  if (
    appointmentVersion === 1 &&
    !(!!outcomeFlags?.appt_auth_f2f_flag && !!appointmentsMeta?.f2f_referral && f2fComplete)
  ) {
    return null;
  }

  return (
    <div>
      {alert?.show && <Alert message={alert.message} type="error" style={{ marginTop: 20 }} showIcon />}
      {patientInfo?.users?.[0]?.email}
      {!selfBook && outcomeUsesReferral && (
        <Form.Item
          name="clinician_postcode"
          data-testid="clinician-postcode"
          label={
            <div>
              <div>{t('Clinician Selected & Task Created')}</div>
              <Typography.Text type="secondary">
                {t("The clinician selected is a recommendation only and doesn't have to be selected")}
              </Typography.Text>
            </div>
          }
          labelCol={{ span: 12 }}
        >
          <div css={{ float: 'right' }}>
            <Button type="link" onClick={() => setf2fOpen(true)} data-testid="view-referral-button">
              {t('View')}
            </Button>
          </div>
        </Form.Item>
      )}
      {selfBook && (
        <>
          {patientInfo?.users?.[0]?.email ? ' - ' : ''}
          {t('Self book Face-to-Face Referral created.')}
        </>
      )}
      <SendGDPREmailFormItem
        needsGDPREmailSending={needsGDPREmailSending}
        sendGDPREmail={sendGDPREmail}
        GDPRConfirmationSent={GDPRConfirmationSent}
      />
      {selfBook && (
        <Button
          style={{ float: 'right' }}
          data-testid="delete-referral-button"
          disabled={isCompleted}
          type="link"
          size="small"
          danger
          onClick={() => setWarnRemoveModalOpen(true)}
        >
          {t('Remove self book authorisation')}
        </Button>
      )}
      {outcomeUsesReferral && (
        <Button
          style={{ float: 'right' }}
          data-testid="preview-letter-button"
          disabled={isCompleted}
          type="link"
          size="small"
          onClick={() => pdfPreview()}
        >
          {previewLoading && <FiLoader className="spinner" />}
          {t('Preview letter')}
        </Button>
      )}
      <ConfirmationModal
        open={warnRemoveModalOpen}
        onCancel={() => setWarnRemoveModalOpen(false)}
        footer={[
          <Button
            key="cancel-remove-f2f-self-book"
            onClick={() => setWarnRemoveModalOpen(false)}
            data-testid="cancel-button"
          >
            {t('Cancel')}
          </Button>,
          <Button
            type="primary"
            danger
            key="confirm-remove-f2f-self-book"
            onClick={() => {
              removeSelfBookAuthAfterReferral();
              setWarnRemoveModalOpen(false);
            }}
            data-testid="confirm-remove-button"
          >
            {t('Remove')}
          </Button>
        ]}
        closable
        title="Are you sure you want to remove self book authorisation?"
      >
        {t('This action cannot be undone.')}
      </ConfirmationModal>

      <CreateF2FTaskModal open={f2fOpen} onClose={() => setf2fOpen(false)} />

      <br />
      <br />
    </div>
  );
};

export default F2fCompletedFields;
